import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Warehouse, TrendingUp, ShoppingBag } from 'lucide-react';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import {
  transformSalesData,
  groupSalesByProduct,
  calculateSalesStats,
} from '../../shared/utils/salesDataUtils';
import {
  transformPurchasesData,
  groupPurchasesByProduct,
  calculatePurchasesStats,
} from '../../shared/utils/purchasesDataUtils';
import {
  isSalesAnalyticsDocument,
  isPurchaseAnalyticsDocument,
} from '../../shared/utils/documentAnalyticsFilters';
import { useDocuments } from '../../core/contexts/DocumentsContext';
import { useClients } from '../../core/contexts/ClientsContext';
import { useProducts } from '../../core/contexts/ProductsContext';

export default function AnalysesRapportsInventaire() {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.analysesRapportsInventaire.${key}`, opts);

  const { documents, refreshData, loading: docsLoading } = useDocuments();
  const { clients } = useClients();
  const { products, refreshData: refreshProducts, loading: productsLoading } = useProducts();

  const loading = docsLoading || productsLoading;

  const stockProducts = useMemo(
    () => (products || []).filter((item) => item.type !== 'service'),
    [products],
  );

  const totalStockUnits = useMemo(
    () => stockProducts.reduce((sum, item) => sum + (parseInt(item.stock, 10) || 0), 0),
    [stockProducts],
  );

  const salesData = useMemo(
    () => (documents || []).filter(isSalesAnalyticsDocument),
    [documents],
  );

  const purchasesData = useMemo(
    () => (documents || []).filter(isPurchaseAnalyticsDocument),
    [documents],
  );

  const transformedSales = useMemo(
    () => transformSalesData(salesData, clients),
    [salesData, clients],
  );

  const transformedPurchases = useMemo(
    () => transformPurchasesData(purchasesData, [], i18n.language),
    [purchasesData, i18n.language],
  );

  const salesStats = useMemo(() => calculateSalesStats(transformedSales), [transformedSales]);
  const purchasesStats = useMemo(
    () => calculatePurchasesStats(transformedPurchases),
    [transformedPurchases],
  );

  const salesByProduct = useMemo(
    () => groupSalesByProduct(transformedSales, products),
    [transformedSales, products],
  );

  const purchasesByProduct = useMemo(
    () => groupPurchasesByProduct(transformedPurchases, products),
    [transformedPurchases, products],
  );

  const totalQtySold = useMemo(
    () => salesByProduct.reduce((sum, row) => sum + (row.quantitySold || 0), 0),
    [salesByProduct],
  );

  const totalQtyPurchased = useMemo(
    () => purchasesByProduct.reduce((sum, row) => sum + (row.quantityPurchased || 0), 0),
    [purchasesByProduct],
  );

  const lowStockCount = useMemo(
    () =>
      stockProducts.filter((item) => {
        const stock = parseFloat(item.stock) || 0;
        const minStock = parseFloat(item.min_stock) || 0;
        return minStock > 0 ? stock <= minStock : stock === 0;
      }).length,
    [stockProducts],
  );

  const activeStockCount = useMemo(
    () => Math.max(0, stockProducts.length - lowStockCount),
    [stockProducts.length, lowStockCount],
  );

  const stockTurnover = useMemo(
    () => (totalStockUnits > 0 ? (totalQtySold / totalStockUnits).toFixed(2) : '—'),
    [totalQtySold, totalStockUnits],
  );

  const salesPurchasesRatio = useMemo(() => {
    if (!totalQtyPurchased) return '—';
    return (totalQtySold / totalQtyPurchased).toFixed(2);
  }, [totalQtySold, totalQtyPurchased]);

  const findProductCode = useCallback(
    (name) => {
      const match = products.find((pr) => pr.name === name);
      return match?.reference || match?.code || '—';
    },
    [products],
  );

  const findProduct = useCallback(
    (name) => products.find((pr) => pr.name === name),
    [products],
  );

  const findStock = useCallback(
    (name) => {
      const match = findProduct(name);
      return match?.stock != null ? String(parseInt(match.stock, 10) || 0) : '—';
    },
    [findProduct],
  );

  const findStockStatus = useCallback(
    (name) => {
      const match = findProduct(name);
      if (!match) return '—';
      const stock = parseFloat(match.stock) || 0;
      const minStock = parseFloat(match.min_stock) || 0;
      const isLow = minStock > 0 ? stock <= minStock : stock === 0;
      return isLow ? p('status_low_stock') : p('status_active');
    },
    [findProduct, p],
  );

  const stats = useMemo(
    () => [
      {
        label: p('kpi_different_products'),
        value: stockProducts.length,
        icon: Package,
        variant: 'forest',
      },
      {
        label: p('kpi_total_quantity'),
        value: totalStockUnits,
        icon: Warehouse,
        variant: 'copper',
      },
      {
        label: p('kpi_revenue'),
        value: formatBrandMoney(salesStats.totalAmount || 0),
        icon: TrendingUp,
        variant: 'forest',
      },
      {
        label: p('kpi_total_amount'),
        value: formatBrandMoney(purchasesStats.totalAmount || 0),
        icon: ShoppingBag,
        variant: 'copper',
      },
    ],
    [stockProducts.length, totalStockUnits, salesStats.totalAmount, purchasesStats.totalAmount, p],
  );

  const insights = useMemo(
    () => [
      {
        label: p('insight_turnover'),
        value: stockTurnover,
        hint: p('insight_turnover_hint'),
      },
      {
        label: p('insight_sales_purchases_ratio'),
        value: salesPurchasesRatio,
        hint: p('insight_sales_purchases_hint'),
      },
      {
        label: p('insight_low_stock'),
        value: lowStockCount,
        hint: p('insight_low_stock_hint'),
      },
    ],
    [stockTurnover, salesPurchasesRatio, lowStockCount, p],
  );

  const charts = useMemo(
    () => [
      {
        title: p('chart_pipeline_title'),
        subtitle: p('chart_pipeline_subtitle'),
        variant: 'pipeline',
        rows: [
          { label: p('kpi_different_products'), value: stockProducts.length },
          { label: p('kpi_total_quantity'), value: totalStockUnits },
          { label: p('col_qty_sold_short'), value: totalQtySold },
          { label: p('col_qty_purchased_short'), value: totalQtyPurchased },
        ],
      },
      {
        title: p('chart_ranked_title'),
        subtitle: p('chart_ranked_subtitle'),
        variant: 'ranked',
        rows: salesByProduct.slice(0, 8).map((row) => ({
          label: row.product,
          value: row.quantitySold || 0,
        })),
      },
      {
        title: p('chart_status_title'),
        subtitle: p('chart_status_subtitle'),
        variant: 'status',
        active: activeStockCount,
        inactive: lowStockCount,
      },
    ],
    [
      stockProducts.length,
      totalStockUnits,
      totalQtySold,
      totalQtyPurchased,
      salesByProduct,
      activeStockCount,
      lowStockCount,
      p,
    ],
  );

  const tableRows = useMemo(() => {
    const names = new Set([
      ...salesByProduct.map((row) => row.product),
      ...purchasesByProduct.map((row) => row.product),
    ]);
    return Array.from(names)
      .map((name) => {
        const sold = salesByProduct.find((row) => row.product === name);
        const bought = purchasesByProduct.find((row) => row.product === name);
        const soldQty = sold?.quantitySold || 0;
        const boughtQty = bought?.quantityPurchased || 0;
        return {
          code: findProductCode(name),
          product: name,
          stock: findStock(name),
          qtySold: String(soldQty),
          qtyPurchased: String(boughtQty),
          netMovement: String(soldQty - boughtQty),
          status: findStockStatus(name),
        };
      })
      .sort(
        (a, b) =>
          (parseInt(b.qtySold, 10) || 0) + (parseInt(b.qtyPurchased, 10) || 0)
          - ((parseInt(a.qtySold, 10) || 0) + (parseInt(a.qtyPurchased, 10) || 0)),
      );
  }, [salesByProduct, purchasesByProduct, findProductCode, findStock, findStockStatus]);

  const table = useMemo(
    () => ({
      title: p('table_profit_detail'),
      columns: [
        { key: 'code', label: p('col_code') },
        { key: 'product', label: p('col_product') },
        { key: 'stock', label: p('col_quantity') },
        { key: 'qtySold', label: p('col_qty_sold_short') },
        { key: 'qtyPurchased', label: p('col_qty_purchased_short') },
        { key: 'netMovement', label: p('col_net_movement') },
        { key: 'status', label: p('col_stock_status') },
      ],
      rows: tableRows,
    }),
    [tableRows, p],
  );

  const handleRefresh = useCallback(async () => {
    await Promise.all([refreshData?.(), refreshProducts?.()]);
  }, [refreshData, refreshProducts]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={handleRefresh}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="inventaire_analytics"
      emptyLabel={p('no_data')}
    />
  );
}
